import React, { useEffect, useState } from "react";
import { api, formatErr, fmtDate, money } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHeader, StatusPill, EmptyState } from "@/components/shared";
import { Plus, FileDown, Trash2, Edit3 } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["Open", "Billed", "Paid", "Disputed"];

const empty = {
  customer_id: "",
  vehicle_id: "",
  plate: "",
  violation_date: "",
  violation_amount: 0,
  admin_fee: 0,
  status: "Open",
  description: "",
};

export default function Tolls() {
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [letter, setLetter] = useState(null);

  const load = async () => {
    const [t, c, v] = await Promise.all([api.get("/tolls"), api.get("/customers"), api.get("/vehicles")]);
    setRows(t.data);
    setCustomers(c.data);
    setVehicles(v.data);
  };
  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing({ ...empty });
    setOpen(true);
  };
  const openEdit = (t) => {
    setEditing({ ...empty, ...t });
    setOpen(true);
  };

  // When vehicle chosen, prefill plate
  const prefillPlate = (vid) => {
    const v = vehicles.find((x) => x.id === vid);
    if (v) setEditing((s) => ({ ...s, vehicle_id: vid, plate: s.plate || v.plate || "" }));
    else setEditing((s) => ({ ...s, vehicle_id: vid }));
  };

  const save = async () => {
    try {
      if (!editing.customer_id || !editing.vehicle_id) return toast.error("Customer and vehicle required");
      const payload = {
        ...editing,
        violation_amount: Number(editing.violation_amount),
        admin_fee: Number(editing.admin_fee),
      };
      const id = payload.id;
      delete payload.id;
      delete payload.created_at;
      delete payload.customer_name;
      delete payload.vehicle_label;
      delete payload.total;
      if (id) {
        await api.put(`/tolls/${id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/tolls", payload);
        toast.success("Toll/Ticket recorded · notice queued");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    try {
      await api.delete(`/tolls/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const showLetter = async (id) => {
    const r = await api.get(`/tolls/${id}/transfer-letter`);
    setLetter(r.data.letter);
  };

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <PageHeader
        title="Tolls & Tickets"
        subtitle="Violations & transfers"
        testId="tolls-page-header"
        action={
          <Button data-testid="add-toll-button" onClick={openNew} className="bg-black hover:bg-gray-800 text-white">
            <Plus size={16} className="mr-2" /> Add Violation
          </Button>
        }
      />

      {rows.length === 0 ? (
        <EmptyState title="No tolls or tickets" testId="tolls-empty" action={<Button onClick={openNew}>Add violation</Button>} />
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="tolls-list">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-left px-4 py-3">Customer</th>
                  <th className="text-left px-4 py-3">Vehicle / Plate</th>
                  <th className="text-right px-4 py-3">Violation</th>
                  <th className="text-right px-4 py-3">Admin fee</th>
                  <th className="text-right px-4 py-3">Total</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((t) => (
                  <tr key={t.id} className="hover:bg-gray-50" data-testid={`toll-row-${t.id}`}>
                    <td className="px-4 py-3">{fmtDate(t.violation_date)}</td>
                    <td className="px-4 py-3 font-medium">{t.customer_name}</td>
                    <td className="px-4 py-3">
                      <div>{t.vehicle_label}</div>
                      <div className="text-xs text-gray-500 font-mono">{t.plate}</div>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{money(t.violation_amount)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{money(t.admin_fee)}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-semibold">{money(t.total)}</td>
                    <td className="px-4 py-3"><StatusPill status={t.status} /></td>
                    <td className="px-2 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" title="Transfer letter" onClick={() => showLetter(t.id)} data-testid={`letter-toll-${t.id}`}>
                          <FileDown size={13} />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openEdit(t)} data-testid={`edit-toll-${t.id}`}>
                          <Edit3 size={13} />
                        </Button>
                        {isAdmin && (
                          <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => del(t.id)} data-testid={`delete-toll-${t.id}`}>
                            <Trash2 size={13} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="toll-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing?.id ? "Edit Violation" : "Add Toll / Ticket"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Customer</Label>
                <Select value={editing.customer_id} onValueChange={(v) => setEditing({ ...editing, customer_id: v })}>
                  <SelectTrigger data-testid="toll-customer-select"><SelectValue placeholder="Choose customer" /></SelectTrigger>
                  <SelectContent>{customers.map((c) => (<SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Vehicle</Label>
                <Select value={editing.vehicle_id} onValueChange={prefillPlate}>
                  <SelectTrigger data-testid="toll-vehicle-select"><SelectValue placeholder="Choose vehicle" /></SelectTrigger>
                  <SelectContent>{vehicles.map((v) => (<SelectItem key={v.id} value={v.id}>{v.year} {v.make} {v.model}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Plate</Label>
                <Input data-testid="toll-plate" value={editing.plate} onChange={(e) => setEditing({ ...editing, plate: e.target.value })} />
              </div>
              <div>
                <Label>Violation date</Label>
                <Input type="date" data-testid="toll-date" value={editing.violation_date} onChange={(e) => setEditing({ ...editing, violation_date: e.target.value })} />
              </div>
              <div>
                <Label>Violation amount ($)</Label>
                <Input type="number" step="0.01" data-testid="toll-amount" value={editing.violation_amount} onChange={(e) => setEditing({ ...editing, violation_amount: e.target.value })} />
              </div>
              <div>
                <Label>Admin fee ($)</Label>
                <Input type="number" step="0.01" data-testid="toll-admin-fee" value={editing.admin_fee} onChange={(e) => setEditing({ ...editing, admin_fee: e.target.value })} />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                  <SelectTrigger data-testid="toll-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label>Description</Label>
                <Textarea rows={2} data-testid="toll-desc" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="toll-cancel">Cancel</Button>
            <Button onClick={save} className="bg-black hover:bg-gray-800 text-white" data-testid="toll-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!letter} onOpenChange={(o) => !o && setLetter(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="letter-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Transfer of Responsibility Letter</DialogTitle>
          </DialogHeader>
          <pre className="whitespace-pre-wrap text-sm bg-gray-50 border border-gray-200 rounded-md p-4 font-mono">{letter}</pre>
          <DialogFooter className="no-print">
            <Button variant="outline" onClick={() => navigator.clipboard.writeText(letter || "")} data-testid="letter-copy">Copy</Button>
            <Button onClick={() => window.print()} className="bg-black hover:bg-gray-800 text-white" data-testid="letter-print">Print</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
